import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from './Login';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const statusConfig = {
  agendado:      { label: 'Agendado',       color: '#f59e0b', bg: '#fef3c7' },
  confirmado:    { label: 'Confirmado',     color: '#3b82f6', bg: '#dbeafe' },
  em_atendimento:{ label: 'Em atendimento', color: '#64748b', bg: '#f1f5f9' },
  concluido:     { label: 'Concluído',      color: '#10b981', bg: '#d1fae5' },
  cancelado:     { label: 'Cancelado',      color: '#ef4444', bg: '#fee2e2' },
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 48px;
  padding-bottom: 5rem;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  padding: 1.5rem 1.25rem 3rem;
  color: white;
  position: relative;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const Greeting = styled.div`
  p { font-size: 0.85rem; opacity: 0.85; margin: 0 0 0.2rem; }
  h1 { font-size: 1.4rem; font-weight: 700; margin: 0; }
`;

const PlanBadge = styled.span`
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.3rem 0.7rem;
  border-radius: 20px;
`;

const NextCard = styled(motion.div)`
  background: linear-gradient(135deg, #334155, #475569);
  border-radius: 14px;
  padding: 0.9rem 1.1rem;
  margin: 0.75rem 1.25rem 1.25rem;
  color: white;
  display: flex; align-items: center; gap: 0.75rem;
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(51,65,85,0.22);
  cursor: pointer;

  .icon { font-size: 1.4rem; flex-shrink: 0; }
  .label { font-size: 0.65rem; font-weight: 700; opacity: 0.8; text-transform: uppercase; letter-spacing: 0.05em; }
  .name  { font-size: 0.95rem; font-weight: 700; margin: 0.15rem 0 0.1rem; }
  .meta  { font-size: 0.75rem; opacity: 0.85; }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  padding: 0 1.25rem;
  position: relative;
  z-index: 1;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  cursor: ${p => p.clickable ? 'pointer' : 'default'};

  .icon { font-size: 1.5rem; margin-bottom: 0.4rem; display: block; }
  .value {
    font-size: 1.6rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1;
    margin-bottom: 0.2rem;
  }
  .label {
    font-size: 0.72rem;
    color: #9ca3af;
    font-weight: 500;
  }
  .badge {
    display: inline-block;
    margin-top: 0.4rem;
    background: #fef3c7;
    color: #d97706;
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    border-radius: 20px;
  }
`;

const Section = styled.div`
  padding: 1.5rem 1.25rem 0;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  h2 { font-size: 1rem; font-weight: 700; color: #1f2937; margin: 0; }
  button {
    font-size: 0.8rem;
    color: #334155;
    font-weight: 600;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
  }
`;

const OrderCard = styled(motion.div)`
  background: white;
  border-radius: 14px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:active { transform: scale(0.98); }
`;

const OrderAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const OrderInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name { font-weight: 600; color: #1f2937; font-size: 0.9rem; }
  .product { font-size: 0.78rem; color: #6b7280; margin-top: 0.15rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const OrderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.3rem;
  flex-shrink: 0;
  .total { font-weight: 700; font-size: 0.9rem; color: #1f2937; }
  .date { font-size: 0.68rem; color: #9ca3af; }
`;

const StatusBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  color: ${p => statusConfig[p.status]?.color || '#6b7280'};
  background: ${p => statusConfig[p.status]?.bg || '#f3f4f6'};
`;

// ─── COMPONENTE ───────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const storeName = user?.storeName || 'Meu Salão';
  const ownerName = user?.ownerName || user?.email?.split('@')[0] || '';
  const plan = user?.plan || 'basico';

  const [stats, setStats] = useState({ hoje: 0, pendentes: 0, faturamentoMes: 0, recentes: [] });
  const [loadingStats, setLoadingStats] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/appointments/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setStats(await r.json());
    } catch {}
    setLoadingStats(false);
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchStats();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchStats]);

  const pedidosRecentes = stats.recentes || [];
  const nextAppt = pedidosRecentes.length > 0 ? pedidosRecentes[0] : null;

  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';

  const formatHour = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const hoje = new Date();
    if (d.getDate() === hoje.getDate() && d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()) {
      return 'Hoje';
    }
    const amanha = new Date(hoje);
    amanha.setDate(amanha.getDate() + 1);
    if (d.getDate() === amanha.getDate() && d.getMonth() === amanha.getMonth() && d.getFullYear() === amanha.getFullYear()) {
      return 'Amanhã';
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <Page>
      <Header>
        <HeaderTop>
          <Greeting>
            <p>{saudacao}, {ownerName}! 👋</p>
            <h1>{storeName}</h1>
          </Greeting>
          <PlanBadge>Plano {plan}</PlanBadge>
        </HeaderTop>
      </Header>

      <CardsGrid style={{ marginTop: '-1.75rem' }}>
        <StatCard
          clickable={1}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/pedidos?status=agendado')}
        >
          <span className="icon">⏳</span>
          <div className="value">{loadingStats ? '…' : stats.pendentes}</div>
          <div className="label">Aguardando confirmação</div>
          {!loadingStats && stats.pendentes > 0 && <span className="badge">Ação necessária</span>}
        </StatCard>

        <StatCard whileTap={{ scale: 0.97 }}>
          <span className="icon">📅</span>
          <div className="value">{loadingStats ? '…' : stats.hoje}</div>
          <div className="label">Atendimentos hoje</div>
        </StatCard>

        <StatCard whileTap={{ scale: 0.97 }}>
          <span className="icon">💰</span>
          <div className="value">R$ {loadingStats ? '…' : (stats.faturamentoMes || 0).toLocaleString('pt-BR')}</div>
          <div className="label">Faturamento do mês</div>
        </StatCard>

        <StatCard whileTap={{ scale: 0.97 }}>
          <span className="icon">📊</span>
          <div className="value">{loadingStats ? '…' : pedidosRecentes.length}</div>
          <div className="label">Próximos agendamentos</div>
        </StatCard>
      </CardsGrid>

      <Section>
        <SectionHeader>
          <h2>Próximos agendamentos</h2>
          <button onClick={() => navigate('/pedidos')}>Ver agenda →</button>
        </SectionHeader>

        {pedidosRecentes.length === 0 && !loadingStats && (
          <p style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
            Nenhum agendamento próximo
          </p>
        )}
        {pedidosRecentes.slice(1).map((order, i) => {
          const dt = new Date(order.datetime);
          const dateStr = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          const timeStr = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          return (
            <OrderCard
              key={order._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              onClick={() => navigate('/pedidos')}
            >
              <OrderAvatar>✂️</OrderAvatar>
              <OrderInfo>
                <div className="name">{order.clientName}</div>
                <div className="product">{order.service}</div>
              </OrderInfo>
              <OrderRight>
                <span className="total">R$ {order.price}</span>
                <StatusBadge status={order.status}>
                  {statusConfig[order.status]?.label}
                </StatusBadge>
                <span className="date">{dateStr} {timeStr}</span>
              </OrderRight>
            </OrderCard>
          );
        })}
      </Section>

      {nextAppt && (
        <NextCard
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/pedidos')}
        >
          <div className="icon">⚡</div>
          <div>
            <div className="label">Próximo agendamento</div>
            <div className="name">{nextAppt.clientName}</div>
            <div className="meta">{nextAppt.service} · {formatDate(nextAppt.datetime)} às {formatHour(nextAppt.datetime)}</div>
          </div>
        </NextCard>
      )}

    </Page>
  );
};

export default Dashboard;